import { graphql, useFragment } from 'react-relay';
import { FC, ReactNode } from 'react';
import { useCheckoutSelector } from '../reducers/root';
import { filterQuotes } from '../helpers/filterQuotes';
import { useDisableShippingSubmit_deliveryOptions$key } from './__generated__/useDisableShippingSubmit_deliveryOptions.graphql';

const deliveryOptionsFragment = graphql`
    fragment useDisableShippingSubmit_deliveryOptions on ShipmentQuote @relay(plural: true) {
        serviceId
        isLocalPickup
        showQuoteAsPending
    }
`;

type HookProps = {
    deliveryOptions: useDisableShippingSubmit_deliveryOptions$key;
    isLoading: boolean;
};
export function useDisableShippingSubmit({
    deliveryOptions: deliveryOptionsRef,
    isLoading,
}: HookProps): boolean {
    const deliveryOptions = useFragment(deliveryOptionsFragment, deliveryOptionsRef);
    const shipmentType = useCheckoutSelector(state => state.transaction.shipmentType);
    const isCustomQuoteSelected = useCheckoutSelector(
        state => state.delivery.isCustomQuoteSelected
    );
    const shipmentQuoteId = useCheckoutSelector(state => state.transaction.shipmentQuoteId);
    const shipmentNotes = useCheckoutSelector(state => state.transaction.shipmentNotes);

    // If a prequote shipment option was removed due to quantity limits, disable continue button until new available shipping method is selected (unless a custom quote is selected)
    const availablePreQuotes = filterQuotes(deliveryOptions) || [];
    const dibsFacilitatedShipping = shipmentType === 'FIRSTDIBS_FACILITATED';
    const disabledUntilNewQuoteSelected =
        //Disabled when there is no shipmentQuoteId attached but there are prequotes available.
        (dibsFacilitatedShipping &&
            !isCustomQuoteSelected &&
            !shipmentQuoteId &&
            !!availablePreQuotes.length) ||
        //Disabled when there is a shipmentQuoteId attached but it does not match any available prequote Ids (unless a custom quote is selected)
        (dibsFacilitatedShipping &&
            shipmentQuoteId &&
            !availablePreQuotes.find(q => q.serviceId === shipmentQuoteId));

    const isDisabled =
        isLoading ||
        (isCustomQuoteSelected && !(shipmentNotes || '').trim()) ||
        disabledUntilNewQuoteSelected;

    return isDisabled;
}

export const DisableShippingProvider: FC<
    HookProps & {
        children: (isShippingDisabled: boolean) => ReactNode;
    }
> = props => {
    const { deliveryOptions, children, isLoading } = props;

    const isShippingDisabled = useDisableShippingSubmit({ deliveryOptions, isLoading });

    return <>{children(isShippingDisabled)}</>;
};
